import React from 'react';

interface JiraSettingsSkeletonProps {
  step?: number;
}

const JiraSettingsSkeleton: React.FC<JiraSettingsSkeletonProps> = ({ step = 1 }) => {
  if (step === 1) {
    return (
      <div className="p-4 md:px-8 md:py-4 flex-1 overflow-y-auto">
        <div className="space-y-6 max-w-3xl">
          {/* Main Info Card Skeleton */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 shadow-sm">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 mt-2">
                <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
              </div>
              <div className="flex-1 space-y-2">
                <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-64 animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 animate-pulse"></div>
              </div>
            </div>

            {/* Features List Skeleton */}
            <div className="mb-6 pl-1 space-y-4">
              <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-32 animate-pulse mb-3"></div>
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse flex-shrink-0 mt-0.5"></div>
                  <div className="space-y-1.5 flex-1">
                    <div className="h-3.5 bg-gray-300 dark:bg-gray-600 rounded w-40 animate-pulse"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-64 animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>

            {/* Input Section Skeleton */}
            <div className="mt-6 space-y-4 border-t border-gray-100 dark:border-gray-700 pt-6">
              <div className="space-y-2">
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-32 animate-pulse"></div>
                <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg w-full animate-pulse"></div>
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-24 animate-pulse"></div>
                <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg w-full animate-pulse"></div>
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-24 animate-pulse"></div>
                <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg w-full animate-pulse"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4 animate-pulse mt-1 ml-1"></div>
              </div>
            </div>
          </div>

          {/* Info Box Skeleton */}
          <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-blue-200 dark:bg-blue-800 rounded-full animate-pulse flex-shrink-0 mt-0.5"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-blue-200 dark:bg-blue-800 rounded w-48 animate-pulse"></div>
                <div className="h-3 bg-blue-100 dark:bg-blue-900 rounded w-full animate-pulse"></div>
                <div className="h-3 bg-blue-200 dark:bg-blue-800 rounded w-32 animate-pulse mt-2"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 2 skeleton
  return (
    <div className="p-4 md:px-8 md:py-4 flex-1 overflow-y-auto">
      <div className="space-y-6">
        {/* Connection Status Skeleton */}
        <div className="flex items-center justify-between px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <div>
              <div className="text-sm text-green-800 dark:text-green-200 font-medium">
                <div className="h-4 bg-green-200 dark:bg-green-800 rounded w-32 animate-pulse"></div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-8 w-24 bg-red-100 dark:bg-red-900/30 rounded-lg animate-pulse"></div>
          </div>
        </div>

        {/* Connected Details Skeleton */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-4 bg-white/90 dark:bg-gray-800/60 shadow-sm">
          <div className="text-sm text-light-text-secondary dark:text-white/70">
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4 max-w-lg mb-4 animate-pulse"></div>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-light-text-tertiary dark:text-white/60">
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-20 animate-pulse"></div>
              </span>
              <span className="font-semibold text-base text-light-text-primary dark:text-white">
                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-64 animate-pulse"></div>
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-light-text-tertiary dark:text-white/60">
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-20 animate-pulse"></div>
              </span>
              <span className="font-semibold text-base text-light-text-primary dark:text-white">
                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-40 animate-pulse"></div>
              </span>
            </div>
          </div>
        </div>

        {/* Webhook Configuration Skeleton */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-5 bg-white dark:bg-gray-800/60 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="shrink-0">
              <div className="w-5 h-5 bg-gray-300 dark:bg-gray-600 rounded-full animate-pulse"></div>
            </div>
            <div>
              <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-48 animate-pulse mb-1"></div>
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-80 animate-pulse"></div>
            </div>
          </div>

          <div className="mt-2 p-4 rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-white/5">
            <div className="ml-5 space-y-2">
              <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-full animate-pulse"></div>
              <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-2/3 animate-pulse"></div>
              <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 animate-pulse"></div>
              <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-1/2 animate-pulse"></div>
              <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-5/6 animate-pulse"></div>
              <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-4/5 animate-pulse"></div>
              <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-1/3 animate-pulse"></div>
            </div>
          </div>
        </div>

        {/* Next Steps Grid */}
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
          <div className="h-5 bg-gray-300 dark:bg-gray-600 rounded w-56 animate-pulse"></div>
        </h3>
        <div className="grid grid-cols-1 gap-4">
          
          {/* Import Projects Box Skeleton */}
          <div className="rounded-xl border border-blue-200 dark:border-blue-800 p-5 bg-white dark:bg-gray-800/60 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="shrink-0">
                <div className="w-5 h-5 bg-blue-200 dark:bg-blue-800 rounded animate-pulse"></div>
              </div>
              <div className="space-y-1">
                <div className="h-4 bg-blue-300 dark:bg-blue-700 rounded w-48 animate-pulse"></div>
                <div className="h-3 bg-blue-200 dark:bg-blue-800 rounded w-80 animate-pulse"></div>
              </div>
            </div>

            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-slate-800/60 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 transition-colors">
                  <div className="flex-1">
                    <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-32 animate-pulse mb-1"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-64 animate-pulse"></div>
                  </div>
                  <div className="h-8 w-32 bg-blue-100 dark:bg-blue-900/50 rounded-lg animate-pulse shrink-0"></div>
                </div>
              ))}
            </div>
          </div>

          {/* AI Analysis Box Skeleton */}
          <div className="rounded-xl border border-blue-200 dark:border-blue-800/50 p-5 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="shrink-0">
                <div className="w-5 h-5 bg-blue-300 dark:bg-blue-700 rounded animate-pulse"></div>
              </div>
              <div className="space-y-1">
                <div className="h-4 bg-blue-400 dark:bg-blue-600 rounded w-48 animate-pulse"></div>
                <div className="h-3 bg-blue-300 dark:bg-blue-700 rounded w-96 animate-pulse"></div>
              </div>
            </div>

            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white/80 dark:bg-slate-800/80 p-3 rounded-lg border border-blue-100 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-500/50 transition-all shadow-sm hover:shadow-md cursor-default group">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-40 animate-pulse"></div>
                  </div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JiraSettingsSkeleton;